import { defaultToolTip } from '@components/charts/config';
import Amount from '@utils/currency';
import D_Number from '@utils/number';
import { wrap } from './waterfallchart';

const getNameIfCategoryIsEmpty = ({ category, name }) => {
  if (category && category.length > 0) {
    return category;
  }
  return name;
};

const getSumOfCategory = (a, category) =>
  a.reduce((acc, e) => {
    if (getNameIfCategoryIsEmpty(e) !== category) {
      return acc;
    }
    return (Number(e?.value) || 0) + acc;
  }, 0);

const formatStackedBarData = (inputData = []) => {
  const categories = inputData
    .map(getNameIfCategoryIsEmpty)
    .filter((e, i, a) => a.indexOf(e) === i)
    .sort(
      (a, b) => getSumOfCategory(inputData, a) - getSumOfCategory(inputData, b),
    );

  const names = inputData
    .map((e) => e.name)
    .filter((e, i, a) => a.indexOf(e) === i);

  const categoryIndexMap = categories.reduce(
    (acc, e, i) => ({ ...acc, [e]: i }),
    {},
  );

  const initialShowCurrency = inputData.some((row) => row.showCurrency);

  const getCategoryIndex = (cat) => categoryIndexMap[cat];

  const seriesDataObject = names.reduce(
    (acc, name) => ({
      ...acc,
      [name]: Array.from(categories, () => null),
    }),
    {},
  );

  inputData.forEach(({ name, category, comment, value }) => {
    const formatY = D_Number.format(Number(value), 'none', -1);
    const dollarValue = Amount.format(
      { value: Number(value), currencyIsoCode: 'USD' },
      -1,
    );
    const groupName = getNameIfCategoryIsEmpty({ category, name });
    seriesDataObject[name][getCategoryIndex(groupName)] = {
      y: parseFloat(value),
      formatY,
      dollarValue,
      comment,
    };
  });

  return {
    seriesData: names.map((e) => ({
      name: e,
      data: seriesDataObject[e],
    })),
    categories,
    showCurrency: initialShowCurrency,
  };
};

export const buildStackedBarChartOption = (data, isLightMode) => {
  const { seriesData, categories, showCurrency } = formatStackedBarData(data);

  const result = {
    chart: {
      type: 'column',
    },
    plotOptions: {
      column: {
        stacking: 'normal',
      },
    },

    title: {
      text: undefined,
    },

    xAxis: {
      categories,
      lineWidth: 0,
    },

    yAxis: {
      title: {
        text: undefined,
      },
      gridLineColor: '#1d1d1d',
      labels: {
        formatter() {
          return null;
        },
      },
      stackLabels: {
        enabled: true,
        style: {
          fontWeight: 'bold',
          color: isLightMode ? '#000' : 'white',
        },
        formatter() {
          return showCurrency
            ? Amount.format({ value: this.total, currencyIsoCode: 'USD' }, -1)
            : D_Number.format(this.total, undefined, -1);
        },
      },
      plotLines: [
        {
          value: 0,
          color: '#333333',
          width: 1,
          zIndex: 5,
        },
      ],
    },

    legend: {
      enabled: false,
    },

    tooltip: {
      ...defaultToolTip,
      enabled: true,
      followPointer: true,
      formatter() {
        const mainPart = `<h1>${this.series.name}</h1>
                <br></br>
                <p>${
                  showCurrency ? this.point.dollarValue : this.point.formatY
                }</p>`;

        const commentPart = `<br></br>
                <p>${wrap(this.point.comment || '')}</p></div>`;

        if (this.point.comment) {
          return `<div> ${mainPart} ${commentPart} </div>`;
        }
        return `<div> ${mainPart} </div>`;
      },
    },

    series: seriesData,

    credits: {
      enabled: false,
    },
  };

  return result;
};
